import { useEffect, useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/providers/auth-provider'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { reportError } from '@/lib/error-reporter'
import { subscribeToMailingList } from '@/lib/queries/onboarding'
import { profile } from '@/lib/queries/profiles'
import { supabase } from '@/lib/supabase/client'
import { passwordFieldsSchema } from '@/lib/validation/password'
import { acceptInviteQueryOptions } from '@/hooks/use-accept-invite-mutation'
import { useAuthActions } from '@/hooks/use-auth-actions'
import { Button, buttonVariants } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PageSpinner } from '@/components/ui/spinner'
import { Logo } from '@/components/logo'
import { PasswordInput } from '@/components/password-input'
import {
  AuthDivider,
  SocialSignIn,
} from '@/features/auth/components/social-sign-in'

const searchSchema = z.object({
  org_id: z.uuid().catch(''),
})

const onboardingSchema = passwordFieldsSchema.safeExtend({
  fullName: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(60, 'Name must be less than 60 characters'),
})

type OnboardingFormValues = z.infer<typeof onboardingSchema>

function AcceptInviteComponent() {
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()

  // Show spinner while auth is initializing
  if (isLoading) {
    return (
      <div className='flex min-h-screen flex-col items-center justify-center'>
        <PageSpinner />
      </div>
    )
  }

  // Redirect to sign-in if not authenticated, then come back here
  if (!user) {
    navigate({
      to: '/sign-in',
      search: { redirect: `${location.pathname}${location.search}` },
      replace: true,
    })
    return null
  }

  return <AuthenticatedAcceptInvite user={user} />
}

function AuthenticatedAcceptInvite({
  user,
}: {
  user: NonNullable<ReturnType<typeof useAuth>['user']>
}) {
  const navigate = useNavigate()
  const { org_id } = Route.useSearch()
  const queryClient = useQueryClient()
  const [isSavingPassword, setIsSavingPassword] = useState(false)
  const { updatePassword } = useAuthActions()

  // Accept invitation via suspense query — idempotent, suspends until complete
  const { data: invitation } = useSuspenseQuery(
    acceptInviteQueryOptions(org_id, user.id)
  )

  // Fetch profile using shared query (same cache key as _authenticated layout)
  const { data: profileData } = useSuspenseQuery(profile.findOne(user.id))

  // Google invitees have no password to set; the app asks for a missing name.
  const signedInWithGoogle =
    user.app_metadata.providers?.includes('google') ?? false
  const needsOnboarding =
    !profileData.onboarding_completed_at && !signedInWithGoogle

  const form = useForm<OnboardingFormValues>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      fullName: '',
      password: '',
      confirmPassword: '',
    },
  })

  // Already onboarded — mark invitation as accepted and redirect to org dashboard
  useEffect(() => {
    if (!needsOnboarding) {
      const name = profileData.full_name
      toast.success(
        name
          ? `Welcome to ${invitation.org_name}, ${name}!`
          : `Welcome to ${invitation.org_name}!`,
        { id: 'invite-welcome' }
      )
      navigate({
        to: '/org/$orgId',
        params: { orgId: invitation.org_id },
        replace: true,
      })
    }
  }, [needsOnboarding, profileData.full_name, invitation, navigate])

  if (!needsOnboarding) {
    return (
      <div className='flex min-h-screen flex-col items-center justify-center'>
        <PageSpinner />
      </div>
    )
  }

  const onSubmit = async (values: OnboardingFormValues) => {
    setIsSavingPassword(true)
    try {
      // Set password
      const result = await updatePassword(values.password)

      if (!result.success) {
        form.setError('password', {
          message: result.error!,
        })
        return
      }

      // Update profile with full name and mark onboarding as complete
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: values.fullName,
          onboarding_completed_at: new Date().toISOString(),
        })
        .eq('id', user.id)

      if (profileError) {
        throw new Error(`Failed to update profile: ${profileError.message}`, {
          cause: profileError,
        })
      }

      if (user.email) {
        subscribeToMailingList(user.email, values.fullName)
      }

      // Invalidate profile query - this triggers re-render
      // The !needsOnboarding check above will handle redirect + toast
      await queryClient.invalidateQueries({ queryKey: ['profile', user.id] })
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to complete onboarding'
      reportError(error, { toast: message })
    } finally {
      setIsSavingPassword(false)
    }
  }

  return (
    <div className='container grid h-svh max-w-none items-center justify-center'>
      <div className='mx-auto flex w-full flex-col justify-center space-y-2 py-8 sm:w-[480px] sm:p-8'>
        <div className='mb-4 flex items-center justify-center'>
          <Logo className='mr-2 w-12' />
          <h1 className='text-xl font-medium'>Lumoo</h1>
        </div>
        <Card className='gap-4'>
          <CardHeader>
            <CardTitle className='text-lg tracking-tight'>
              Welcome to Lumoo
            </CardTitle>
            <CardDescription>
              {invitation.inviter_name} has invited you to join{' '}
              {invitation.org_name}. <br />
              Continue with Google, or set a password.
            </CardDescription>
          </CardHeader>
          <CardContent className='flex flex-col gap-4'>
            <SocialSignIn
              redirectTo={`${location.pathname}${location.search}`}
            />
            <p className='text-muted-foreground text-meta -mt-2'>
              Use the Google account for {user.email}.
            </p>
            <AuthDivider />
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className='space-y-4'
              >
                <FormField
                  control={form.control}
                  name='fullName'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl>
                        <Input
                          type='text'
                          placeholder='Enter your full name'
                          autoComplete='name'
                          disabled={isSavingPassword}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='password'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <PasswordInput
                          placeholder='Enter a strong password'
                          disabled={isSavingPassword}
                          autoComplete='new-password'
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Must be at least 8 characters.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='confirmPassword'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm Password</FormLabel>
                      <FormControl>
                        <PasswordInput
                          placeholder='Confirm your password'
                          disabled={
                            isSavingPassword ||
                            !form.formState.dirtyFields.password
                          }
                          autoComplete='new-password'
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type='submit'
                  className='w-full'
                  disabled={isSavingPassword}
                >
                  {isSavingPassword && (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  )}
                  Continue
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

// Acceptance fails when the invitation is for another account (the usual
// case: already signed in as someone else), expired, or revoked.
function AcceptInviteError() {
  const { user } = useAuth()
  const { signOut } = useAuthActions()

  return (
    <div className='container grid h-svh max-w-none items-center justify-center'>
      <div className='mx-auto flex w-full flex-col justify-center space-y-2 py-8 sm:w-[480px] sm:p-8'>
        <div className='mb-4 flex items-center justify-center'>
          <Logo className='mr-2 w-12' />
          <h1 className='text-xl font-medium'>Lumoo</h1>
        </div>
        <Card className='gap-4'>
          <CardHeader>
            <CardTitle className='text-lg tracking-tight'>
              Invitation Unavailable
            </CardTitle>
            <CardDescription>
              {user?.email
                ? `There is no pending invitation to this organization for ${user.email}. If it was sent to another address, sign out and sign in with that account.`
                : 'This invitation has expired or was revoked. Ask the sender to invite you again.'}
            </CardDescription>
          </CardHeader>
          <CardContent className='flex flex-col gap-2'>
            {user && (
              <Button className='w-full' onClick={() => signOut()}>
                Sign out
              </Button>
            )}
            <Link
              to='/'
              className={buttonVariants({
                variant: 'outline',
                className: 'w-full',
              })}
            >
              Go to Lumoo
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export const Route = createFileRoute('/auth/accept-invite')({
  component: AcceptInviteComponent,
  errorComponent: AcceptInviteError,
  validateSearch: searchSchema,
})
